/**
 * The Value board — what each enquiry was quoted at, what it cost, and the margin (Akhil,
 * 2026-09-17: "where is the price for each enquiry"). Facts and sums in `lib/analytics/value.ts`.
 *
 * Every figure is in the COMPANY's currency so enquiries compare; the customer's own currency
 * and amount sit beside it where they differ. Sorted by whichever column is clicked; each row
 * opens its quotation. The four headline sums follow the page's date range like every other
 * board.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Badge } from '../../components/Badge'
import { BoardCard } from './BoardCard'
import { formatMoney } from '../../lib/format'
import type { ValueFact, ValueSummary } from '../../lib/analytics/value'

type SortKey = 'date' | 'quoted' | 'margin' | 'customer'

const STATUS_TONE: Record<string, 'muted' | 'info' | 'ok' | 'danger'> = { Draft: 'muted', Quoted: 'info', Won: 'ok', Rejected: 'danger', Cancelled: 'muted' }

export function ValueBoard({
  facts,
  summary,
  isLoading,
  error,
}: {
  facts: ValueFact[] | undefined
  summary: ValueSummary | undefined
  isLoading: boolean
  error: unknown
}) {
  const navigate = useNavigate()
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'date', dir: -1 })
  // The table's own status filter and search (Akhil, 2026-09-19) — on top of the page's.
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const statuses = useMemo(() => [...new Set((facts ?? []).map((f) => f.status).filter(Boolean))].sort(), [facts])
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    const list = (facts ?? []).filter(
      (f) =>
        (status === '' || f.status === status) &&
        (term === '' || f.enquiry.toLowerCase().includes(term) || f.customer_name.toLowerCase().includes(term) || f.quotation.toLowerCase().includes(term)),
    )
    const by: Record<SortKey, (f: ValueFact) => string | number> = {
      date: (f) => f.date,
      quoted: (f) => f.quoted_base.amount,
      margin: (f) => f.margin_pct ?? -Infinity,
      customer: (f) => f.customer_name.toLowerCase(),
    }
    const pick = by[sort.key]
    return list.sort((a, b) => {
      const x = pick(a)
      const y = pick(b)
      return (x < y ? -1 : x > y ? 1 : 0) * sort.dir
    })
  }, [facts, sort, status, search])

  const header = (key: SortKey, label: string, align?: 'right') => (
    <th
      className={align}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => setSort((s) => ({ key, dir: s.key === key ? ((s.dir * -1) as 1 | -1) : -1 }))}
      title="Sort"
    >
      {label}
      {sort.key === key ? (sort.dir === -1 ? ' ▾' : ' ▴') : ''}
    </th>
  )

  return (
    <BoardCard
      title="Value by enquiry"
      subtitle="Quoted value, PO cost and margin per enquiry — from each quotation, as stored on it. Company currency; the customer's own beside it where it differs."
      isLoading={isLoading}
      error={error}
      isEmpty={(facts?.length ?? 0) === 0}
      emptyLabel="No quotations in this range."
      actions={
        (facts?.length ?? 0) > 0 ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="search"
              value={search}
              placeholder="enquiry, customer, quotation…"
              onChange={(e) => setSearch(e.target.value)}
              style={{ fontSize: 12, padding: '3px 8px', width: 200 }}
            />
            <select value={status} onChange={(e) => setStatus(e.target.value)} title="Status" style={{ fontSize: 12, padding: '3px 6px' }}>
              <option value="">Status: any</option>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>
              {rows.length} of {facts?.length ?? 0}
            </span>
          </div>
        ) : undefined
      }
    >
      {summary ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, margin: '10px 0 12px' }}>
          <Kpi label={`Quoted · ${summary.count}`} value={formatMoney(summary.quoted_total)} />
          <Kpi label={`Won · ${summary.won_count}`} value={formatMoney(summary.won_total)} tone="ok" />
          <Kpi label="Still open" value={formatMoney(summary.open_total)} tone="info" />
          <Kpi label="Lost" value={formatMoney(summary.lost_total)} tone="danger" />
          <Kpi
            label={`Margin over PO cost · ${summary.costed_count} with a cost`}
            value={summary.costed_count > 0 ? `${formatMoney(summary.margin_total)}${summary.margin_pct !== null ? ` · ${summary.margin_pct}%` : ''}` : '—'}
            tone={summary.margin_total.amount >= 0 ? 'ok' : 'danger'}
          />
        </div>
      ) : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Enquiry</th>
              {header('customer', 'Customer')}
              <th>Status</th>
              {header('date', 'Date')}
              {header('quoted', 'Quoted', 'right')}
              <th className="right">PO cost</th>
              {header('margin', 'Margin', 'right')}
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.quotation} style={{ cursor: 'pointer' }} onClick={() => navigate(`/quote/${f.enquiry}`)} title={`Open ${f.quotation}`}>
                <td className="mono">
                  {f.enquiry}
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{f.quotation}</div>
                </td>
                <td style={{ whiteSpace: 'normal' }}>{f.customer_name}</td>
                <td>
                  <Badge tone={STATUS_TONE[f.status] ?? 'muted'}>{f.status || '—'}</Badge>
                </td>
                <td className="mono">{f.date || '—'}</td>
                <td className="right mono">
                  {formatMoney(f.quoted_base)}
                  {f.quoted.currency !== f.quoted_base.currency ? <div style={{ fontSize: 11, color: 'var(--muted)' }}>{formatMoney(f.quoted)}</div> : null}
                </td>
                <td className="right mono">{f.po_cost.amount > 0 ? formatMoney(f.po_cost) : '—'}</td>
                <td className="right mono" style={{ color: f.margin.amount < 0 ? 'var(--danger)' : undefined }}>
                  {f.po_cost.amount > 0 ? `${formatMoney(f.margin)} · ${f.margin_pct}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </BoardCard>
  )
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'info' | 'danger' }) {
  const color = tone === 'ok' ? 'var(--ok)' : tone === 'danger' ? 'var(--danger)' : tone === 'info' ? 'var(--brand)' : 'var(--ink)'
  return (
    <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '8px 12px' }}>
      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontWeight: 700, color, fontSize: 15 }}>{value}</div>
    </div>
  )
}
